import type { LocationQuery, LocationQueryValue } from 'vue-router'
import { format, isValid, parse } from 'date-fns'

/**
 * Estado da Timeline na URL (Onda 2.3, spec §5.2): `unidade=<id>` e `dia=yyyy-MM-dd`.
 * O dia é a âncora do dashboard (uma fonte de verdade): `dia` só a espelha na URL e a
 * hidrata ao abrir um link. Padrão `matrix/matrixState.ts`.
 */
export type TimelineQueryState = { unitId: number | null; day: string | null }

/** Chaves da URL que são estado da Timeline (nunca filtro da API de `GET /stations`). */
export const TIMELINE_QUERY_KEYS: readonly string[] = ['unidade', 'dia']

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const firstValue = (value: LocationQueryValue | LocationQueryValue[] | undefined): string | null =>
  Array.isArray(value) ? (value[0] ?? null) : (value ?? null)

/** `Date` local → `'yyyy-MM-dd'`. */
export const toDayKey = (date: Date): string => format(date, 'yyyy-MM-dd')

/** `'yyyy-MM-dd'` válido (dia real do calendário) → `Date` local às 00:00; senão `null`. */
export function parseDayKey(value: unknown): Date | null {
  if (typeof value !== 'string' || !DAY_PATTERN.test(value)) return null
  const date = parse(value, 'yyyy-MM-dd', new Date())
  // `parse` aceita 2026-02-30 como inválido; a volta garante a forma canônica.
  return isValid(date) && toDayKey(date) === value ? date : null
}

export function parseTimelineQuery(query: LocationQuery): TimelineQueryState {
  const unit = firstValue(query.unidade)
  const unitId = unit && /^\d+$/.test(unit.trim()) ? Number(unit.trim()) : null
  const day = firstValue(query.dia)
  return {
    unitId: unitId && unitId > 0 ? unitId : null,
    day: parseDayKey(day) ? (day as string) : null
  }
}

export function serializeTimelineQuery(state: TimelineQueryState): Record<string, string> {
  const out: Record<string, string> = {}
  if (state.unitId) out.unidade = String(state.unitId)
  if (state.day) out.dia = state.day
  return out
}

export function stripTimelineKeys<T extends Record<string, unknown>>(query: T): T {
  const copy: Record<string, unknown> = { ...query }
  TIMELINE_QUERY_KEYS.forEach((key) => delete copy[key])
  return copy as T
}

export interface TimelineUnitOption {
  id: number
  name: string
}

/** Unidade do `list-view` (valor em string no store de unidades). */
type ListedUnit = { title: string; value: string | number }

/**
 * Opções do seletor (spec §5.2): as unidades do filtro do dashboard, se houver; senão todas
 * da lista. Sempre em ordem pt-BR. Filtro sem nenhuma unidade da lista (ids fora do acesso)
 * cai na lista inteira — o seletor nunca fica sem saída.
 */
export function timelineUnitOptions(
  units: ListedUnit[],
  filterIds: readonly (string | number)[] | undefined
): TimelineUnitOption[] {
  const all = units
    .map((unit) => ({ id: Number(unit.value), name: unit.title }))
    .filter((unit) => Number.isInteger(unit.id) && unit.id > 0)
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR') || a.id - b.id)
  const filter = new Set((filterIds ?? []).map(Number))
  const filtered = all.filter((unit) => filter.has(unit.id))
  return filtered.length ? filtered : all
}

/**
 * Unidade efetiva: a pedida (URL/seletor) se estiver nas opções; senão a última usada na
 * sessão (se ainda estiver nas opções); senão a primeira do filtro (na ordem do filtro);
 * senão a primeira da lista em pt-BR; sem opções, `null`.
 */
export function resolveTimelineUnit(
  requested: number | null,
  options: TimelineUnitOption[],
  filterIds: readonly (string | number)[] | undefined,
  remembered: number | null = null
): number | null {
  const isOption = (id: number | null): id is number =>
    id !== null && options.some((unit) => unit.id === id)
  if (isOption(requested)) return requested
  if (isOption(remembered)) return remembered
  const firstFiltered = (filterIds ?? []).map(Number).find(isOption)
  return firstFiltered ?? options[0]?.id ?? null
}
